import { all, takeEvery, takeLatest, put, call, select } from 'redux-saga/effects';
import FileSaver from 'file-saver';
import { getClaimHistory } from '../claim/slice';
import {
  getWorklistClaimMessages,
  getWorklistClaimRecommendations,
  setWorklistClaimNewMessageAttachmentsUploadLoading
} from '../worklistClaims/slice';
import { selectClaim } from '../claim/selectors';
import renderNotification from '../../helpers/notifications/renderNotification';
import { NOTIFICATION_MESSAGES } from '../../localization/Notifications/dictionary';
import { uploadFileMessageAttachment } from '../fileUpload/slice';
import { ATTACHMENT_MAX_SIZES } from '../../consts/consts';
import { getFileNameFromApiResponseHeader, isEdgeBrowser } from '../../helpers/utility';
import { selectSettlements } from './selectors';
import {
  getSettlements,
  getSettlementsSucceeded,
  getSettlementsFailed,
  createSettlement,
  createSettlementSucceeded,
  createSettlementFailed,
  updateSettlement,
  updateSettlementSucceeded,
  updateSettlementFailed,
  getSettlementPdfSummary,
  getSettlementPdfSummarySucceeded,
  getSettlementPdfSummaryFailed,
  exportSettlements,
  exportSettlementsSucceeded,
  exportSettlementsFailed,
  getCutbackReasons,
  getCutbackReasonsSucceeded,
  getCutbackReasonsFailed,
  createCutbackReason,
  createCutbackReasonSucceeded,
  createCutbackReasonFailed
} from './slice';

import {
  onCreateSettlement,
  onUpdateSettlement,
  onGetSettlements,
  onGetCutbackReasons,
  onCreateCutbackReason,
  onGetSettlementPdfSummary,
  onExportSettlements
} from './api';

export function* getSettlementsWorker({ payload }) {
  const { params } = payload;
  try {
    const response = yield call(onGetSettlements, params);
    const settlements = response.data;
    yield put(getSettlementsSucceeded(settlements));
  } catch {
    yield put(getSettlementsFailed());
  }
}

export function* reloadSettlements() {
  const claim = yield select(selectClaim);

  if (!claim?.id) {
    return;
  }

  const params = { claim_id: claim.id, paging: false };

  yield put(getSettlements({ params }));
  yield put(getClaimHistory({ claimId: claim.id }));
  yield put(getWorklistClaimMessages({ claimId: claim.id }));
  yield put(getWorklistClaimRecommendations({ claimId: claim.id }));
}

export function* createSettlementWorker({ payload }) {
  const { settlement, callback } = payload;
  try {
    yield call(onCreateSettlement, settlement);
    yield put(createSettlementSucceeded());
    callback?.();
  } catch (error) {
    yield put(createSettlementFailed(error));
  }
}

export function* updateSettlementWorker({ payload }) {
  const { settlement, callback } = payload;
  try {
    yield call(onUpdateSettlement, settlement);
    yield put(updateSettlementSucceeded());
    callback?.();
  } catch {
    yield put(updateSettlementFailed());
  }
}

export function* getCutbackReasonsWorker() {
  try {
    const response = yield call(onGetCutbackReasons);
    yield put(getCutbackReasonsSucceeded(response.data));
  } catch {
    yield put(getCutbackReasonsFailed());
  }
}

export function* createCutbackReasonWorker({ payload }) {
  const { cutbackReason } = payload;
  try {
    yield call(onCreateCutbackReason, cutbackReason);
    yield put(createCutbackReasonSucceeded());
    const { settlement_new_cutback_reason_create_success } = NOTIFICATION_MESSAGES;
    renderNotification({ type: 'success', message: settlement_new_cutback_reason_create_success });
    yield put(getCutbackReasons());
  } catch {
    yield put(createCutbackReasonFailed());
  }
}

export function* getSettlementPdfSummaryWorker({ payload }) {
  const { settlementId, isMessage } = payload;
  try {
    const response = yield call(onGetSettlementPdfSummary, settlementId);
    const blobToFile = (theBlob, fileName) => {
      const b = theBlob;
      b.lastModifiedDate = new Date();
      b.name = fileName;
      return b;
    };
    let pdfSettlementFile;

    const fileName = getFileNameFromApiResponseHeader(response.headers, '.pdf');
    const blob = new Blob([response.data], { type: 'application/pdf' });

    // only IE or old Edge do support this method
    if (isEdgeBrowser || window.navigator.msSaveOrOpenBlob) {
      pdfSettlementFile = blobToFile(response.data, fileName);
    } else {
      pdfSettlementFile = new File([blob], fileName, { type: 'application/pdf' });
    }

    yield put(getSettlementPdfSummarySucceeded());

    if (isMessage) {
      const isFileBiggerThanAllowed = pdfSettlementFile.size > ATTACHMENT_MAX_SIZES.default.byte;
      if (isFileBiggerThanAllowed) {
        yield put(setWorklistClaimNewMessageAttachmentsUploadLoading(false));

        const settlements = yield select(selectSettlements);
        const currentSettlement = settlements.find((settlement) => settlement.id === settlementId);

        const settlement_pdf_report_too_big_error = `Die Rechnung '${currentSettlement.reference_number}' ist zu groß, um sie einer Nachricht hinzuzufügen. Die maximale Dateigröße beträgt ${ATTACHMENT_MAX_SIZES.default.megaByte} MB.`;

        renderNotification({ type: 'error', message: settlement_pdf_report_too_big_error });
      } else {
        yield put(
          uploadFileMessageAttachment({
            file: pdfSettlementFile,
            attachmentId: settlementId,
            attachmentType: 'settlement'
          })
        );
      }
    }
  } catch {
    yield put(getSettlementPdfSummaryFailed());
  }
}

export function* exportSettlementsWorker({ payload }) {
  const { params } = payload;
  try {
    const response = yield call(onExportSettlements, params);
    const filename = getFileNameFromApiResponseHeader(response.headers, '.xlsx');

    if (window.navigator.msSaveOrOpenBlob) {
      const blob = new Blob([response.data], { type: 'application/pdf' });
      window.navigator.msSaveBlob(blob, filename);
    } else {
      const blob = new File([response.data], filename, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64'
      });
      FileSaver.saveAs(blob);
    }

    yield put(exportSettlementsSucceeded());
  } catch {
    yield put(exportSettlementsFailed());
  }
}

export default function* settlementsSaga() {
  yield all([
    takeLatest(getSettlements.type, getSettlementsWorker),
    takeLatest(createSettlement.type, createSettlementWorker),
    takeEvery(createSettlementSucceeded.type, reloadSettlements),
    takeLatest(updateSettlement.type, updateSettlementWorker),
    takeEvery(updateSettlementSucceeded.type, reloadSettlements),
    takeLatest(getCutbackReasons.type, getCutbackReasonsWorker),
    takeLatest(createCutbackReason.type, createCutbackReasonWorker),
    takeEvery(getSettlementPdfSummary.type, getSettlementPdfSummaryWorker),
    takeEvery(exportSettlements.type, exportSettlementsWorker)
  ]);
}
